'use client'
import { useEffect, useRef } from 'react'

export type ClusterPin = {
  id: number
  slug: string
  name: string
  lat: number
  lng: number
  imageUrl: string | null
}

export function ClustersPageMap({ pins }: { pins: ClusterPin[] }) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<any>(null)

  useEffect(() => {
    const el = mapRef.current
    if (!el || mapInstance.current || el.classList.contains('leaflet-container')) return

    import('leaflet').then(L => {
      if (!mapRef.current || mapRef.current.classList.contains('leaflet-container')) return

      const map = L.map(mapRef.current!, {
        zoomControl: true,
        scrollWheelZoom: true,
        attributionControl: false,
        dragging: true,
      })
      mapInstance.current = map

      // Topographic tile layer
      L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        maxZoom: 15,
        opacity: 0.88,
      }).addTo(map)

      const markers: any[] = []

      pins.forEach(pin => {
        const initial = pin.name.charAt(0).toUpperCase()
        const photo = pin.imageUrl
          ? `<img src="${pin.imageUrl}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" alt="${pin.name}" />`
          : `<div style="width:100%;height:100%;border-radius:50%;background:#4C6B50;display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;font-weight:700;font-family:serif;">${initial}</div>`

        const html = `
<a href="/clusters/${pin.slug}" style="display:flex;align-items:center;gap:8px;text-decoration:none;pointer-events:all;cursor:pointer;">
  <div style="position:relative;flex:none;">
    <div style="width:64px;height:64px;border-radius:50%;overflow:hidden;border:3px solid #fff;box-shadow:0 2px 12px rgba(0,0,0,.35);">
      ${photo}
    </div>
    <svg style="position:absolute;bottom:-17px;left:50%;transform:translateX(-50%);" width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#4C6B50"/>
      <circle cx="7" cy="7" r="3" fill="#fff"/>
    </svg>
  </div>
  <div style="background:rgba(255,255,255,.95);padding:5px 11px;border-radius:5px;font-size:12.5px;font-weight:600;color:#1E1A16;box-shadow:0 2px 8px rgba(0,0,0,.18);white-space:nowrap;font-family:'Spectral',Georgia,serif;letter-spacing:.01em;backdrop-filter:blur(4px);">${pin.name}</div>
</a>`

        const icon = L.divIcon({
          className: '',
          html,
          iconSize: [230, 84],
          iconAnchor: [32, 84],
        })

        const marker = L.marker([pin.lat, pin.lng], { icon })
        marker.addTo(map)
        markers.push(marker)
      })

      if (markers.length > 0) {
        const group = L.featureGroup(markers)
        map.fitBounds(group.getBounds().pad(0.28))
      } else {
        map.setView([39.5127, 46.3543], 11)
      }
    })

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove()
        mapInstance.current = null
      }
    }
  }, [])

  return (
    <>
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </>
  )
}
